import { fabric } from "fabric";
import { ImageLoadTask } from "./types";
import { ViewportHelper } from "./ViewportHelper";
import { TaskQueue } from "./TaskQueue";
import { RenderScheduler } from "./RenderScheduler";
import { ImageDownloader } from "./ImageDownloader";

export * from "./types";

export class ImageLoader {
  private static instance: ImageLoader;

  private viewportHelper: ViewportHelper;
  private taskQueue: TaskQueue;
  private renderScheduler: RenderScheduler;

  private activeCount: number = 0;
  private maxConcurrent: number = 6; // 限制最大并发数为 6
  private maxRetries: number = 2; // 最大重试次数
  private canvas: fabric.Canvas | null = null;
  private isProcessing: boolean = false;
  private isPaused: boolean = false;

  // 【关键修复】用于追踪队列清空版本，防止过期重试任务污染新队列
  private clearVersion: number = 0;

  // 【埋点支持】空闲等待者集合，用于追踪图片加载完成事件
  private idleWaiters: Set<() => void> = new Set();

  private constructor() {
    this.viewportHelper = new ViewportHelper();
    this.taskQueue = new TaskQueue(this.viewportHelper);
    this.renderScheduler = new RenderScheduler();

    // 【埋点支持】设置渲染调度器的空闲回调
    this.renderScheduler.setOnIdleCallback(() => {
      this.notifyIdleWaiters();
    });
  }

  public static getInstance(): ImageLoader {
    if (!ImageLoader.instance) {
      ImageLoader.instance = new ImageLoader();
    }
    return ImageLoader.instance;
  }

  /**
   * 设置关联的画布实例
   */
  public setCanvas(canvas: fabric.Canvas) {
    this.canvas = canvas;
    this.viewportHelper.setCanvas(canvas);
    this.renderScheduler.setCanvas(canvas);
  }

  /**
   * 暂停队列处理
   */
  public stop() {
    this.isPaused = true;
  }

  /**
   * 等待所有图片加载完成
   * 【埋点支持】用于追踪画布图片加载完成事件
   * @returns Promise<'idle' | 'cleared'> 返回 idle 表示正常完成，cleared 表示队列被清空
   */
  public waitForIdle(): Promise<"idle" | "cleared"> {
    const startVersion = this.clearVersion;
    if (this.isIdle()) return Promise.resolve("idle");

    return new Promise((resolve) => {
      const waiter = () => {
        if (startVersion !== this.clearVersion) {
          this.idleWaiters.delete(waiter);
          resolve("cleared");
          return;
        }
        if (this.isIdle()) {
          this.idleWaiters.delete(waiter);
          resolve("idle");
        }
      };

      this.idleWaiters.add(waiter);
    });
  }

  /**
   * 开始处理队列
   */
  public start() {
    this.isPaused = false;
    this.processQueue();
  }

  /**
   * 添加任务到队列
   */
  public addTask(placeholder: fabric.Object, originalObj: any, index: number = 0) {
    this.taskQueue.add(placeholder, originalObj, index);

    if (!this.isPaused && !this.isProcessing) {
      this.processQueue();
    }
  }

  /**
   * 批量添加任务
   */
  public addTasks(tasks: { placeholder: fabric.Object; originalObj: any; index?: number }[]) {
    this.taskQueue.addBatch(tasks);

    if (!this.isPaused && !this.isProcessing) {
      this.processQueue();
    }
  }

  /**
   * 根据当前视口更新任务优先级
   */
  public updatePriorities() {
    this.taskQueue.updatePriorities();
  }

  /**
   * 更新所有任务的视口状态
   */
  public updateViewportStatus(): void {
    this.taskQueue.updateViewportStatus();
  }

  /**
   * 提升指定图片的优先级为 Gallery 级别
   */
  public promoteGalleryImages(urls: string[], inViewportMap?: Map<string, boolean>) {
    this.taskQueue.promoteGalleryImages(urls, inViewportMap);
  }

  /**
   * 获取当前队列中所有任务的统计信息
   */
  public getQueueStats() {
    return this.taskQueue.getStats();
  }

  /**
   * 清空队列
   */
  public clear() {
    this.taskQueue.clear();
    this.renderScheduler.clear();
    this.activeCount = 0;
    this.isProcessing = false;
    // 【关键修复】增加版本号，用于丢弃过期的重试任务
    this.clearVersion++;
  }

  /**
   * 重试所有失败的任务
   */
  public retryFailed() {
    if (!this.canvas) return;

    const objects = this.canvas.getObjects();
    objects.forEach((obj) => {
      if ((obj as any).isPlaceholder && (obj as any).data?.error) {
        // 重置状态
        obj.set({
          fill: "#2a2a2c",
          stroke: "#3a3a3c",
          strokeWidth: 1
        });
        delete (obj as any).data.error;

        // 重新加入队列
        const originalObj = (obj as any).data.originalObj;
        this.addTask(obj, originalObj);
      }
    });
    this.canvas.requestRenderAll();
  }

  /**
   * 处理队列
   */
  private processQueue() {
    if (this.isPaused) {
      this.isProcessing = false;
      this.notifyIdleWaiters();
      return;
    }

    if (this.taskQueue.isEmpty() && this.activeCount === 0) {
      this.isProcessing = false;
      this.notifyIdleWaiters();
      return;
    }

    this.isProcessing = true;

    // 填充并发槽位
    while (this.activeCount < this.maxConcurrent && !this.taskQueue.isEmpty()) {
      if (this.isPaused) break;

      const task = this.taskQueue.shift();
      if (task) {
        this.activeCount++;
        this.loadTask(task).finally(() => {
          this.activeCount--;
          // 继续处理队列
          if (!this.isPaused) {
            this.processQueue();
          }
        });
      }
    }
  }

  /**
   * 执行单个加载任务
   */
  private loadTask(task: ImageLoadTask): Promise<void> {
    return new Promise((resolve) => {
      // 【关键修复】记录当前清空版本，用于判断重试任务是否过期
      const currentClearVersion = this.clearVersion;

      // 检查是否还有有效的映射（可能都被用户删除了）
      // 【P1 修复】canvas 为 null 时不应视为"全部有效"，而是无法处理，直接跳过避免无效下载
      const validMappings = task.mappings.filter((m) => this.canvas && this.canvas.contains(m.placeholder));
      if (validMappings.length === 0) {
        console.log(`[ImageLoader] 任务 ${task.id} 的所有映射已不在画布上，跳过加载`);
        resolve();
        return;
      }

      // 获取原始链接作为 fallback
      // 假设所有映射指向同一个资源，取第一个即可
      const originalSrc = validMappings[0]?.originalObj?.originalSrc;

      ImageDownloader.download(task.id, originalSrc)
        .then((imgElem) => {
          if (!this.canvas) {
            resolve();
            return;
          }

          // 【核心优化】不再直接操作 canvas，而是推入更新队列
          this.renderScheduler.schedule(validMappings, imgElem);
          resolve();
        })
        .catch((err) => {
          console.warn(`[ImageLoader] 图片加载失败 (尝试 ${task.retries + 1}/${this.maxRetries}): ${task.id}`, err);

          if (task.retries < this.maxRetries) {
            // 重新加入队列尾部，增加重试计数
            task.retries++;
            // 稍微延迟重试，避免立即再次失败
            setTimeout(() => {
              // 【关键修复】检查队列是否已被清空（版本号变化），如果是则丢弃过期任务
              if (currentClearVersion !== this.clearVersion) {
                console.log(`[ImageLoader] 丢弃过期重试任务: ${task.id}`);
                return;
              }
              this.taskQueue.push(task);
              // 重新触发处理
              if (!this.isProcessing) {
                this.processQueue();
              }
            }, 1000 * task.retries);
          } else {
            // 超过重试次数，标记所有映射为失败状态
            validMappings.forEach(({ placeholder }) => {
              if (this.canvas && this.canvas.contains(placeholder)) {
                placeholder.set({
                  fill: "#3a1a1c",
                  stroke: "red",
                  strokeWidth: 2
                });
                (placeholder as any).data = { ...(placeholder as any).data, error: true };
              }
            });
            if (this.canvas) {
              this.canvas.requestRenderAll();
            }
          }
          resolve();
        });
    });
  }

  /**
   * 检查是否处于空闲状态
   * 【埋点支持】用于判断所有图片是否加载完成
   * 【性能优化】供光标追踪器等在加载期间跳过昂贵更新
   */
  public isIdle(): boolean {
    return (
      !this.isPaused &&
      this.taskQueue.isEmpty() &&
      this.activeCount === 0 &&
      this.renderScheduler.isIdle()
    );
  }

  /**
   * 通知所有空闲等待者
   * 【埋点支持】在队列处理完成时触发回调
   */
  private notifyIdleWaiters() {
    if (this.idleWaiters.size === 0) return;
    this.idleWaiters.forEach((fn) => fn());
  }
}

export const imageLoader = ImageLoader.getInstance();
